import React from 'react';
import {
    Popover,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider,
    IconButton,
    Button,
    Avatar,
    CircularProgress
} from '@mui/material';
import {
    Check as CheckIcon,
    Notifications as NotificationsIcon,
    Info as InfoIcon,
    CheckCircle as SuccessIcon,
    Warning as WarningIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

interface Notification {
    id: string;
    title: string;
    message: string;
    type: string;
    isRead: boolean;
    createdAt: string;
}

interface Props {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    notifications: Notification[];
    loading: boolean;
    onMarkAsRead: (id: string) => void;
    onClearAll: () => void;
}

const NotificationPopover: React.FC<Props> = ({
    open,
    anchorEl,
    onClose,
    notifications,
    loading,
    onMarkAsRead,
    onClearAll
}) => {

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'SUCCESS': return '#2e7d32';
            case 'WARNING': return '#ed6c02';
            case 'ERROR': return '#d32f2f';
            default: return '#0288d1';
        }
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'SUCCESS': return <SuccessIcon sx={{ fontSize: 18, color: '#2e7d32' }} />;
            case 'WARNING': return <WarningIcon sx={{ fontSize: 18, color: '#ed6c02' }} />;
            case 'ERROR': return <ErrorIcon sx={{ fontSize: 18, color: '#d32f2f' }} />;
            default: return <InfoIcon sx={{ fontSize: 18, color: '#0288d1' }} />;
        }
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            PaperProps={{
                sx: {
                    width: 360,
                    maxHeight: 480,
                    mt: 1.5,
                    borderRadius: '20px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                    border: '1px solid #f0f0f0'
                }
            }}
        >
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8faff' }}>
                <Typography variant="h6" fontWeight={800} color="#1a237e">Notifications</Typography>
                <Box>
                    <Button
                        size="small"
                        onClick={onClearAll}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                        disabled={notifications.length === 0}
                    >
                        Clear All
                    </Button>
                </Box>
            </Box>
            <Divider />

            <Box sx={{ overflowY: 'auto', maxHeight: 380 }}>
                {loading && notifications.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : notifications.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <NotificationsIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
                        <Typography color="text.secondary">No new notifications</Typography>
                    </Box>
                ) : (
                    <List sx={{ p: 0 }}>
                        {notifications.map((notif) => (
                            <ListItem
                                key={notif.id}
                                sx={{
                                    borderBottom: '1px solid #f5f5f5',
                                    bgcolor: notif.isRead ? 'transparent' : 'rgba(26, 35, 126, 0.02)',
                                    transition: 'all 0.2s',
                                    '&:hover': { bgcolor: '#f5f7ff' }
                                }}
                                secondaryAction={
                                    !notif.isRead && (
                                        <IconButton size="small" onClick={() => onMarkAsRead(notif.id)} title="Mark as read">
                                            <CheckIcon fontSize="small" />
                                        </IconButton>
                                    )
                                }
                            >
                                <Box sx={{ mr: 2, mt: 0.5 }}>
                                    <Avatar sx={{ bgcolor: `${getTypeColor(notif.type)}15`, width: 32, height: 32 }}>
                                        {getTypeIcon(notif.type)}
                                    </Avatar>
                                </Box>
                                <ListItemText
                                    primary={
                                        <Typography variant="body2" fontWeight={notif.isRead ? 600 : 800} color="#1B2559">
                                            {notif.title}
                                        </Typography>
                                    }
                                    secondary={
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                {notif.message}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#A3AED0', fontSize: '10px' }}>
                                                {formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true })}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>
        </Popover>
    );
};

export default NotificationPopover;
